import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { DatePicker, Popover, Tooltip } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { CalendarOutlined } from '@ant-design/icons';

interface DuAnCongViecDatetimePickerProps {
  value?: [Dayjs | null, Dayjs | null];
  onChange?: (dates: [Dayjs | null, Dayjs | null]) => void;
  // Parent range: nếu có thì không check year, chỉ giới hạn trong parent range (dùng cho parent picker)
  parentRange?: { start: Dayjs | null; end: Dayjs | null } | null;
  // Initial range: giới hạn cho child picker (chỉ được chọn trong range của parent)
  initialRange?: [Dayjs, Dayjs] | null;
  // Giới hạn cho parent khi đã có childs:
  // - startDate không được lớn hơn minStartAllowed (min ngày bắt đầu của các con)
  // - endDate không được nhỏ hơn maxEndAllowed (max ngày kết thúc của các con)
  minStartAllowed?: Dayjs | null;
  maxEndAllowed?: Dayjs | null;
  year?: number; // Năm hiện tại (nếu không có parent range thì check year)
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** z-index popover chọn ngày — tăng khi đặt trong lớp phủ cao hơn popover mặc định (1030). */
  popupZIndex?: number;
}

const parseDateValue = (value?: string | Date | Dayjs | null): Dayjs | null => {
  if (!value) return null;
  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;
  const parsed = dayjs(value, ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ'], true);
  if (parsed.isValid()) return parsed;
  const isoParsed = dayjs(value);
  return isoParsed.isValid() ? isoParsed : null;
};

export const DuAnCongViecDatetimePicker: React.FC<DuAnCongViecDatetimePickerProps> = ({
  value,
  onChange,
  parentRange = null,
  initialRange = null,
  minStartAllowed = null,
  maxEndAllowed = null,
  year = new Date().getFullYear(),
  disabled = false,
  className = '',
  style = {},
  popupZIndex,
}) => {

  const [open, setOpen] = React.useState(false);
  const [startDate, setStartDate] = React.useState<Dayjs | null>(value?.[0] || null);
  const [endDate, setEndDate] = React.useState<Dayjs | null>(value?.[1] || null);
  const startDateRef = useRef<Dayjs | null>(value?.[0] || null);
  const endDateRef = useRef<Dayjs | null>(value?.[1] || null);

  useEffect(() => {
    if (value) {
      setStartDate(value[0]);
      setEndDate(value[1]);
      startDateRef.current = value[0];
      endDateRef.current = value[1];
    }
  }, [value]);

  useEffect(() => {
    startDateRef.current = startDate;
  }, [startDate]);

  useEffect(() => {
    endDateRef.current = endDate;
  }, [endDate]);

  React.useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = `
      .duan-congviec-datetime-picker-panel-only .ant-picker-input {
        display: none !important;
      }
      .duan-congviec-datetime-picker-panel-only .ant-picker-panel {
        margin-top: 0 !important;
        position: static !important;
        box-shadow: none !important;
        background: #fff !important;
      }
      .duan-congviec-datetime-picker-panel-only .ant-picker {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
      }
      .duan-congviec-datetime-picker-wrapper {
        position: relative;
      }
      .duan-congviec-datetime-picker-wrapper .ant-picker-dropdown {
        position: static !important;
        inset: auto !important;
        transform: none !important;
        box-shadow: none !important;
      }
      .duan-congviec-datetime-picker-wrapper .ant-picker-panel-container {
        box-shadow: none !important;
      }

      /* Make "other month" dates red + bold.
         Popup calendar is rendered inside .duan-congviec-datetime-picker-wrapper .ant-picker-dropdown */
      .duan-congviec-datetime-picker-wrapper .ant-picker-dropdown .ant-picker-content tbody td.ant-picker-cell:not(.ant-picker-cell-in-view),
      .duan-congviec-datetime-picker-wrapper .ant-picker-dropdown .ant-picker-content tbody td.ant-picker-cell:not(.ant-picker-cell-in-view) .ant-picker-cell-inner {
        opacity: 1 !important;
      }
      .duan-congviec-datetime-picker-wrapper .ant-picker-dropdown .ant-picker-content tbody td.ant-picker-cell:not(.ant-picker-cell-in-view) .ant-picker-cell-inner,
      .duan-congviec-datetime-picker-wrapper .ant-picker-dropdown .ant-picker-content tbody td.ant-picker-cell-disabled:not(.ant-picker-cell-in-view) .ant-picker-cell-inner {
        color: rgba(113, 108, 108, 0.9) !important;
        font-weight: 400 !important;
      }
    `;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  const startOfSelectedYear = useMemo(() => dayjs(`${year}-01-01`), [year]);
  const endOfSelectedYear = useMemo(() => dayjs(`${year}-12-31`), [year]);
  const hasParentRange = !!parentRange?.start || !!parentRange?.end;
  const hasInitialRange = !!initialRange && initialRange.length === 2;
  const hasChilds = !!minStartAllowed || !!maxEndAllowed;
  // Ưu tiên check parent trước
  const minDate = hasParentRange
    ? (parentRange?.start || startOfSelectedYear)
    : hasInitialRange
      ? initialRange[0]
      : startOfSelectedYear;

  const maxDate = hasParentRange
    ? (parentRange?.end || endOfSelectedYear)
    : hasInitialRange
      ? initialRange[1]
      : endOfSelectedYear;

  // Format display: cùng ngày / cùng tháng / cùng năm / khác năm
  const formatDisplayText = (start: Dayjs | null, end: Dayjs | null): string => {
    if (!start || !end) return '';
    const startDay = String(start.date()).padStart(2, '0');
    const startMonth = String(start.month() + 1).padStart(2, '0');
    const startYear = start.year();
    const endDay = String(end.date()).padStart(2, '0');
    const endMonth = String(end.month() + 1).padStart(2, '0');
    const endYear = end.year();

    // Cùng ngày
    if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
      return `${startDay}/${startMonth}`;
    }
    // Cùng tháng cùng năm: 01-15/07
    if (startYear === endYear && startMonth === endMonth) {
      return `${startDay}-${endDay}/${startMonth}`;
    }
    // Cùng năm khác tháng: 01/07-15/08
    if (startYear === endYear) {
      return `${startDay}/${startMonth}-${endDay}/${endMonth}`;
    }
    // Khác năm: 01/07/25-15/08/26 (năm 2 số — gọn hơn trên card hẹp)
    const sy = String(startYear).slice(-2);
    const ey = String(endYear).slice(-2);
    return `${startDay}/${startMonth}/${sy}-${endDay}/${endMonth}/${ey}`;
  };

  const displayText = formatDisplayText(startDate, endDate);

  /** Hover: luôn hiện đủ khoảng thời gian DD/MM/YYYY - DD/MM/YYYY */
  const fullRangeTooltip = useMemo(() => {
    if (!startDate?.isValid() || !endDate?.isValid()) return '';
    return `${startDate.format('DD/MM/YYYY')} - ${endDate.format('DD/MM/YYYY')}`;
  }, [startDate, endDate]);

  const handleStartDateChange = (date: Dayjs | null) => {
    startDateRef.current = date;
    setStartDate(date);
    onChange?.([date, endDateRef.current]);
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    endDateRef.current = date;
    setEndDate(date);
    onChange?.([startDateRef.current, date]);
  };

  // Shared logic: kiểm tra date có nằm trong range cho phép không
  const isDateInAllowedRange = useCallback(
    (date: Dayjs): boolean => {
      if (!hasInitialRange && !hasParentRange && date.year() !== year) return false;
      if (date.isBefore(minDate, 'day') || date.isAfter(maxDate, 'day')) return false;
      return true;
    },
    [hasInitialRange, hasParentRange, maxDate, minDate, year]
  );

  // Disable cho ô "Bắt đầu"
  const disabledStartDate = useCallback(
    (current: Dayjs): boolean => {
      if (!current) return false;
      if (!isDateInAllowedRange(current)) return true;
      if (hasChilds) {
        if (minStartAllowed) {
          const minStart = parseDateValue(minStartAllowed);
          if (minStart && current.isAfter(minStart, 'day')) {
            if (maxEndAllowed) {
              const maxEnd = parseDateValue(maxEndAllowed);
              if (maxEnd && current.isBefore(maxEnd, 'day')) return true;
            }
            return true;
          }
        }
      }
      if (endDate && current.isAfter(endDate, 'day')) return true;
      return false;
    },
    [endDate, hasChilds, isDateInAllowedRange, minStartAllowed]
  );

  // Disable cho ô "Kết thúc": giữ logic childs; cho phép chọn trùng ngày bắt đầu
  const disabledEndDate = useCallback(
    (current: Dayjs): boolean => {
      if (!current) return false;
      if (!isDateInAllowedRange(current)) return true;
      // Logic childs: end không được nhỏ hơn maxEndAllowed (cha phải bao trùm con)
      if (hasChilds) {
        if (maxEndAllowed) {
          const maxEnd = parseDateValue(maxEndAllowed);
          if (maxEnd && current.isBefore(maxEnd, 'day')) {
            if (minStartAllowed) {
              const minStart = parseDateValue(minStartAllowed);
              if (minStart && current.isAfter(minStart, 'day')) return true;
            }
            return true;
          }
        }
      }
      // Chỉ disable các ngày trước ngày bắt đầu (cho phép chọn cùng ngày bắt đầu)
      if (startDate && current.isBefore(startDate, 'day')) return true;
      return false;
    },
    [hasChilds, isDateInAllowedRange, maxEndAllowed, minStartAllowed, startDate]
  );

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Đẩy cặp ngày hiện tại ra ngoài khi đóng panel (tránh stale state / thiếu onChange khi chọn lần lượt)
      onChange?.([startDateRef.current, endDateRef.current]);
    }
    setOpen(newOpen);
  };

  const content = (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 20,
        rowGap: 16,
        padding: 16,
        width: 'min(636px, calc(100vw - 48px))',
        minWidth: 0,
        maxHeight: 'calc(100vh - 200px)',
        overflow: 'auto',
      }}
    >
      <div style={{ flex: '1 1 280px', minWidth: 260, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '-8px', gap: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)', minWidth: '70px' }}>
            Bắt đầu
          </div>
          <div style={{ flex: 1, pointerEvents: 'none' }}>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              format="DD/MM/YYYY"
              disabledDate={disabledStartDate}
              style={{ width: '100%' }}
              bordered
              size="small"
            />
          </div>
        </div>
        <div className="duan-congviec-datetime-picker-wrapper">
          <DatePicker
            value={startDate}
            onChange={handleStartDateChange}
            format="DD/MM/YYYY"
            disabledDate={disabledStartDate}
            open
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            style={{ width: '100%' }}
            bordered={false}
            className="duan-congviec-datetime-picker-panel-only"
          />
        </div>
      </div>
      <div style={{ flex: '1 1 280px', minWidth: 260, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '-8px', gap: '12px' }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(0, 0, 0, 0.85)', minWidth: '70px' }}>
            Kết thúc
          </div>
          <div style={{ flex: 1, pointerEvents: 'none' }}>
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              format="DD/MM/YYYY"
              disabledDate={disabledEndDate}
              style={{ width: '100%' }}
              bordered
              size="small"
            />
          </div>
        </div>
        <div className="duan-congviec-datetime-picker-wrapper">
          <DatePicker
            value={endDate}
            onChange={handleEndDateChange}
            format="DD/MM/YYYY"
            disabledDate={disabledEndDate}
            open
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            style={{ width: '100%' }}
            bordered={false}
            className="duan-congviec-datetime-picker-panel-only"
          />
        </div>
      </div>
    </div>
  );
  
  return (
    <Popover
      content={content}
      trigger="click"
      open={open && !disabled}
      onOpenChange={handleOpenChange}
      placement="bottomLeft"
      getPopupContainer={() => document.body}
      zIndex={popupZIndex}
      // overlayStyle={{ padding: 0, maxWidth: 'calc(100vw - 16px)' }}
      overlayInnerStyle={{
        padding: 0,
        height: 'auto',
        maxHeight: 'calc(100vh - 160px)',
        overflow: 'visible',
        borderRadius: '8px',
        boxShadow:
          '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Tooltip title={fullRangeTooltip || undefined} placement="top">
        <div
          className={`w-full cursor-pointer ${className}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
            minHeight: '32px',
            border: 'none',
            boxShadow: 'none',
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            ...style,
          }}
        >
          <CalendarOutlined style={{ color: '#42b814', fontSize: style?.fontSize ?? 14 }} />
          <span
            style={{
              marginLeft: '4px',
              lineHeight: 1.2,
              color: displayText ? 'rgba(0, 0, 0, 0.85)' : 'rgba(0, 0, 0, 0.25)',
              ...((typeof style?.fontSize === 'number' || typeof style?.fontSize === 'string')
                ? { fontSize: style.fontSize }
                : {}),
            }}
          >
            {displayText || 'Chọn ngày'}
          </span>
        </div>
      </Tooltip>
    </Popover>
  );
};

export default DuAnCongViecDatetimePicker;
